import {
  Card,
  CardContent,
  Skeleton,
  cn,
  statusBoardInkRowClass,
  statusBoardInkSurfaceClass,
} from '@up2stay/up2promo-ui';
import { motion } from 'motion/react';
import { CheckCircle2, ChevronRight } from 'lucide-react';

import { dashboardSpringSnappy } from './dashboard-board-primitives';
import type { DashboardDoNextItem } from './dashboard-status-types';

function badgeClasses(severity: DashboardDoNextItem['severity']): string {
  switch (severity) {
    case 'critical':
      return 'bg-destructive/15 text-destructive dark:bg-[#3a1e14] dark:text-[#f7a77e]';
    case 'warning':
      return 'bg-amber-500/15 text-amber-800 dark:bg-[#3a2a12] dark:text-[#f5c66b]';
    case 'neutral':
      return 'bg-muted text-muted-foreground dark:bg-[#2b2c33] dark:text-[#e9e8e4]';
    default:
      return 'bg-[color:var(--brand-500)] text-white';
  }
}

export function DashboardHeuteZuTunSkeleton({ label }: { label: string }) {
  return (
    <Card
      aria-busy="true"
      aria-label={label}
      className={cn(
        'overflow-hidden rounded-[18px] py-0',
        statusBoardInkSurfaceClass,
      )}
    >
      <CardContent className="space-y-3 p-5">
        <Skeleton className="h-5 w-36 dark:bg-white/10" />
        <Skeleton className="h-14 w-full rounded-xl dark:bg-white/10" />
        <Skeleton className="h-14 w-full rounded-xl dark:bg-white/10" />
        <Skeleton className="h-14 w-full rounded-xl dark:bg-white/10" />
      </CardContent>
    </Card>
  );
}

export function DashboardHeuteZuTun({
  title,
  openCountLabel,
  empty,
  items,
  onOpen,
  reduceMotion,
  isMobile,
}: {
  title: string;
  openCountLabel: string;
  empty: string;
  items: DashboardDoNextItem[];
  onOpen: (href: string) => void;
  reduceMotion: boolean;
  isMobile: boolean;
}) {
  return (
    <section
      aria-labelledby="dashboard-heute-heading"
      className="flex h-full min-w-0 flex-col"
    >
      <Card
        className={cn(
          'h-full overflow-hidden py-0 shadow-[var(--shadow-card)]',
          isMobile ? 'rounded-[20px]' : 'rounded-[18px]',
          statusBoardInkSurfaceClass,
        )}
      >
        <CardContent
          className={cn(
            'flex flex-col',
            isMobile ? 'gap-3 p-4' : 'gap-3.5 p-5',
          )}
        >
          <div className="flex items-center gap-2.5">
            <h2
              id="dashboard-heute-heading"
              className="min-w-0 flex-1 text-[19px] font-bold leading-tight tracking-tight text-foreground dark:text-[#f4f3ef]"
            >
              {title}
            </h2>
            <span className="font-mono text-xs tabular-nums text-muted-foreground dark:text-[#a3a4ab]">
              {openCountLabel}
            </span>
          </div>

          {items.length === 0 ? (
            <div className="flex items-center gap-3 rounded-xl px-3 py-3.5 text-[13.5px] font-medium text-foreground dark:text-[#f4f3ef]">
              <CheckCircle2 className="size-4 shrink-0 text-[color:var(--ok)]" />
              {empty}
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {items.map((item, index) => {
                const badge =
                  item.count != null && item.count > 0
                    ? item.count > 99
                      ? '99+'
                      : String(item.count)
                    : String(index + 1);
                return (
                  <motion.li
                    key={item.id}
                    initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      ...dashboardSpringSnappy,
                      delay: Math.min(0.04 + index * 0.04, 0.24),
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => onOpen(item.href)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-xl px-3 text-left transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-500)]',
                        statusBoardInkRowClass,
                        isMobile ? 'min-h-14 py-3' : 'min-h-11 py-3',
                      )}
                    >
                      <span
                        className={cn(
                          'flex size-[34px] shrink-0 items-center justify-center rounded-[10px] font-mono text-[13px] font-medium tabular-nums',
                          badgeClasses(item.severity),
                        )}
                        aria-hidden
                      >
                        {badge}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[14px] font-medium leading-snug text-foreground dark:text-[#f4f3ef] md:text-[15px]">
                          {item.title}
                        </span>
                        {item.subtitle ? (
                          <span className="mt-0.5 block text-[12px] leading-snug text-muted-foreground dark:text-[#a3a4ab]">
                            {item.subtitle}
                          </span>
                        ) : null}
                      </span>
                      <ChevronRight
                        className="size-4 shrink-0 text-muted-foreground dark:text-[#8e8f96]"
                        aria-hidden
                      />
                    </button>
                  </motion.li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
